import type { BuildingDesign } from '@shared/models/design/building-design.model';
import type { Project } from '@shared/models/project.model';
import type { PdfGeometryImportResponse } from '@shared/models/design/pdf-geometry-import.model';
import {
  designMatchesGeometryImport,
  importDesignFromPdf,
} from '@shared/helpers/design/design-import.helpers';
import { isBeforeDate } from '@shared/helpers/date.helpers';
import { preserveBlueprintImagesFromPrevious } from '@shared/helpers/design/blueprint-underlay-transfer.helpers';
import { UpdateClientAction } from '@client/store/store.model';
import {
  fetchProject,
  useProjectStateStore,
  waitForProjectUpdateQueueIdle,
} from '@client/store/project/project.store';
import { runSerializedDesignPersist } from './design-persist-lock.helpers';
import { assertDesignPersistProjectIsActive } from './design-persist-project.helpers';

const GEOMETRY_IMPORT_PERSIST_FAILED =
  'Imported geometry could not be saved. Please try again.';
const GEOMETRY_IMPORT_PROJECT_CHANGED =
  'Geometry import was cancelled because the active project changed.';
const GEOMETRY_IMPORT_PERSIST_ATTEMPTS = 2;

type PersistImportedDesign = (
  design: BuildingDesign,
  options?: { skipDesignPersistLock?: boolean },
) => Promise<unknown>;

type PersistGeometryImportDeps = {
  projectId: Project['id'];
  getDesign: () => BuildingDesign;
  updateDesign: PersistImportedDesign;
  /** Target building for {@link importDesignFromPdf}; omitted means first building. */
  buildingId?: string;
};

/**
 * Cancel geometry import persistence when its initiating project is no longer active.
 */
const assertGeometryImportProjectIsActive = (projectId: Project['id']): void =>
  assertDesignPersistProjectIsActive({
    projectId,
    errorMessage: GEOMETRY_IMPORT_PROJECT_CHANGED,
  });

/**
 * Align the open project's optimistic-lock token with the server before the
 * import PUT. When {@link fetchProject} is newer than the project captured after
 * {@link waitForProjectUpdateQueueIdle}, the store is replaced with that row so
 * the import does not 422. Image refs are copied from that captured project via
 * {@link preserveBlueprintImagesFromPrevious} — a socket snapshot at the same
 * timestamp often omits them.
 *
 * Do not skip the write because the store already matches the GET timestamp:
 * that is the raster-restore case. A GET older than the open project is dropped
 * by {@link useProjectStateStore} `updateProjectLocally` (stale-socket guard).
 * A same-client PUT during the GET cannot make this GET newer than `live`;
 * `live` is read immediately before the request.
 */
const syncLiveProjectPersistTokenFromServer = async (
  projectId: Project['id'],
): Promise<void> => {
  assertGeometryImportProjectIsActive(projectId);
  await waitForProjectUpdateQueueIdle();
  assertGeometryImportProjectIsActive(projectId);
  const live = useProjectStateStore.getState().project;
  let fresh: Project | undefined;
  try {
    fresh = await fetchProject(projectId);
  } catch {
    assertGeometryImportProjectIsActive(projectId);
    // Persist with the live token rather than failing the import on a GET error.
    return;
  }
  assertGeometryImportProjectIsActive(projectId);
  // Compare to `live`, not the current store: equal timestamps still need
  // image-ref restore. Older GETs are dropped by the stale-socket guard.
  if (!fresh || !isBeforeDate(live.updated_at, fresh.updated_at)) return;
  useProjectStateStore.getState().updateProjectLocally({
    action: UpdateClientAction.Update,
    itemOrId: preserveBlueprintImagesFromPrevious(live, fresh),
  });
};

/**
 * Persist a geometry-import result onto the live design.
 * Waits for in-flight design persists and project PUTs, then aligns
 * `updated_at` with the server so this write is not built on a stale token.
 * Token sync keeps pre-GET image refs and will not roll back a newer
 * in-memory project (store stale-socket guard).
 * A discarded first write is reapplied once to fresh server truth; the bounded
 * retry treats this explicit import as the user's latest geometry decision.
 */
export const persistGeometryImport = async (
  importResponse: PdfGeometryImportResponse,
  deps: PersistGeometryImportDeps,
): Promise<void> => {
  assertGeometryImportProjectIsActive(deps.projectId);
  await runSerializedDesignPersist(async () => {
    assertGeometryImportProjectIsActive(deps.projectId);
    for (
      let attempt = 0;
      attempt < GEOMETRY_IMPORT_PERSIST_ATTEMPTS;
      attempt++
    ) {
      await syncLiveProjectPersistTokenFromServer(deps.projectId);
      // Re-read after every wait so server and underlay updates are retained.
      await deps.updateDesign(
        importDesignFromPdf(importResponse, deps.getDesign(), deps.buildingId),
        { skipDesignPersistLock: true },
      );
      assertGeometryImportProjectIsActive(deps.projectId);
      if (designMatchesGeometryImport(deps.getDesign(), importResponse)) return;
    }
    throw new Error(GEOMETRY_IMPORT_PERSIST_FAILED);
  });
};
